/**
 * Application-wide constants for endpoints, statuses, and configuration
 */

// API Endpoints
export const ENDPOINTS = {
  AUTH: {
    SIGNUP: '/auth/signup/',
    LOGIN: '/auth/login/',
    LOGOUT: '/auth/logout/',
    PROFILE: '/auth/profile/',
    TOKEN_REFRESH: '/auth/refresh/',
    COMPANY_USERS: '/auth/company/users/',
    PENDING_ACCOUNTS: '/auth/pending-accounts/',
    APPROVED_USERS: '/auth/approved-users/',
    APPROVE_ACCOUNT: (id) => `/auth/approve-account/${id}/`,
    REJECT_ACCOUNT: (id) => `/auth/reject-account/${id}/`,
    ARCHIVE_ACCOUNT: (id) => `/auth/archive-account/${id}/`,
    UNARCHIVE_ACCOUNT: (id) => `/auth/unarchive-account/${id}/`,
    IMPERSONATE: '/auth/impersonate/',
    CREDIT_BALANCE: '/credit-balance/',
  },
  PRODUCTS: {
    LIST: '/products/',
    DETAIL: (id) => `/products/${id}/`,
    VARIANTS: '/products/variants/',
    VARIANT_DETAIL: (id) => `/products/variants/${id}/`,
    PUBLIC: {
      LIST: '/public/products/',
    },
    EMPLOYEE: {
      LIST: '/employee/products/',
      DETAIL: (id) => `/employee/products/${id}/`,
      CHOICES: '/employee/products/choices/',
      REACTIVATE: (id) => `/employee/products/${id}/reactivate/`,
      SET_INVENTORY_STATUS: (id) => `/employee/products/${id}/set-inventory-status/`,
      BULK_SET_CATEGORY: '/employee/products/bulk_set_category/',
      VARIANT_LABEL: (variantId) => `/employee/variants/${variantId}/label/`,
      VARIANT_LABEL_HTML: (variantId) => `/employee/variants/${variantId}/label-html/`,
      VARIANT_UPLOAD_LABEL: (variantId) => `/employee/variants/${variantId}/upload_label/`,
      VARIANT_DELETE_LABEL: (variantId) => `/employee/variants/${variantId}/delete_label/`,
      PRODUCT_IMAGE: (id) => `/employee/products/${id}/image/`,
      PRODUCT_UPLOAD_IMAGE: (id) => `/employee/products/${id}/upload_image/`,
      PRODUCT_DELETE_IMAGE: (id) => `/employee/products/${id}/delete_image/`,
    },
    IMAGE: (id) => `/products/${id}/image/`,
  },
  ORDERS: {
    CUSTOMER: {
      LIST: '/orders/',
      DETAIL: (id) => `/orders/${id}/`,
      INVOICE_LINKS: (id) => `/orders/${id}/invoice-links/`,
    },
    EMPLOYEE: {
      LIST: '/employee/orders/',
      DETAIL: (id) => `/employee/orders/${id}/`,
      UPDATE_STATUS: (id) => `/employee/orders/${id}/update-status/`,
      BULK_UPDATE_STATUS: '/employee/orders/bulk-update-status/',
      MERGE: '/employee/orders/merge/',
      UPDATE_NOTES: (id) => `/employee/orders/${id}/update-notes/`,
      UPDATE_ORDER: (id) => `/employee/orders/${id}/update-order/`,
      STATS: '/employee/orders/stats/',
      QR_CODE: (id) => `/employee/orders/${id}/qr-code/`,
      INVOICE_LINKS: (id) => `/employee/orders/${id}/invoice-links/`,
      CREATE_INVOICE: (id) => `/employee/orders/${id}/create-invoice/`,
      SEND_INVOICE: (id) => `/employee/orders/${id}/send-invoice/`,
      RESEND_INVOICE: (id) => `/employee/orders/${id}/resend-invoice/`,
      MARK_PAID_OUTSIDE_STRIPE: (id) => `/employee/orders/${id}/mark-paid-outside-stripe/`,
      VOID_INVOICE: (id) => `/employee/orders/${id}/void-invoice/`,
      SET_DISCOUNT: (id) => `/employee/orders/${id}/set-discount/`,
    },
    SQUARESPACE: '/employee/squarespace-orders/',
  },
  SESSIONS: {
    CUSTOMER: { LIST: '/sessions/' },
    EMPLOYEE: { LIST: '/employee/sessions/' },
  },
  MILL_EMAILS: {
    EMPLOYEE: {
      LIST: '/employee/mill-emails/',
      SEND: '/employee/mill-emails/send/',
      REMIND: '/employee/mill-emails/remind/',
      TEST: '/employee/mill-emails/test/',
    },
  },
  GROWERS: {
    EMPLOYEE: {
      LIST: '/employee/growers/',
      DETAIL: (id) => `/employee/growers/${id}/`,
      CHOICES: '/employee/growers/choices/',
    },
  },
  GRAIN_VARIETIES: {
    EMPLOYEE: {
      LIST: '/employee/grain-varieties/',
      DETAIL: (id) => `/employee/grain-varieties/${id}/`,
    },
  },
  COMPANIES: {
    LIST: '/companies/',
    DETAIL: (id) => `/companies/${id}/`,
    ORDERS: (id) => `/companies/${id}/orders/`,
    USERS: (id) => `/companies/${id}/users/`,
  },
  ADDRESSES: {
    LIST: '/addresses/',
    DETAIL: (id) => `/addresses/${id}/`,
    SET_DEFAULT: (id) => `/addresses/${id}/set_default/`,
  },
  BILLING_CONTACTS: {
    LIST: '/billing-contacts/',
    DETAIL: (id) => `/billing-contacts/${id}/`,
    SET_PRIMARY: (id) => `/billing-contacts/${id}/set-primary/`,
  },
  NOTES: {
    LIST: '/notes/',
    DETAIL: (id) => `/notes/${id}/`,
  },
};

// Order Status Constants (must match backend Order.STATUS_CHOICES)
export const ORDER_STATUSES = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  IN_PROGRESS: 'in_progress',
  FULFILLED: 'fulfilled',
  OUT_FOR_DELIVERY: 'out_for_delivery',
  DELIVERED: 'delivered',
  READY_FOR_PICKUP: 'ready_for_pickup',
  PICKED_UP: 'picked_up',
  CANCELLED: 'cancelled',
};

export const VALID_ORDER_STATUSES = Object.values(ORDER_STATUSES);

export const ORDER_STATUS_LABELS = {
  [ORDER_STATUSES.PENDING]: 'Pending',
  [ORDER_STATUSES.CONFIRMED]: 'Confirmed',
  [ORDER_STATUSES.IN_PROGRESS]: 'In Progress',
  [ORDER_STATUSES.FULFILLED]: 'Fulfilled',
  [ORDER_STATUSES.OUT_FOR_DELIVERY]: 'Out for Delivery',
  [ORDER_STATUSES.DELIVERED]: 'Delivered',
  [ORDER_STATUSES.READY_FOR_PICKUP]: 'Ready for Pickup',
  [ORDER_STATUSES.PICKED_UP]: 'Picked Up',
  [ORDER_STATUSES.CANCELLED]: 'Cancelled',
};

export const ORDER_STATUS_COLORS = {
  [ORDER_STATUSES.PENDING]: '#FFA500', // Orange
  [ORDER_STATUSES.CONFIRMED]: '#4169E1', // Royal Blue
  [ORDER_STATUSES.IN_PROGRESS]: '#9370DB', // Medium Purple
  [ORDER_STATUSES.FULFILLED]: '#32CD32', // Lime Green
  [ORDER_STATUSES.OUT_FOR_DELIVERY]: '#20B2AA', // Light Sea Green
  [ORDER_STATUSES.DELIVERED]: '#228B22', // Forest Green
  [ORDER_STATUSES.READY_FOR_PICKUP]: '#E6890A', // Amber
  [ORDER_STATUSES.PICKED_UP]: '#2E7D32', // Dark Green
  [ORDER_STATUSES.CANCELLED]: '#DC143C', // Crimson
};

// Payment status display labels (used on customer Invoices page and OrderDetail)
export const PAYMENT_STATUS_LABELS = {
  open: 'Open',
  paid: 'Paid',
  overdue: 'Overdue',
  payment_failed: 'Payment Failed',
  voided: 'Voided',
};

// User Roles
export const USER_ROLES = {
  CUSTOMER: 'customer',
  EMPLOYEE: 'employee',
};

// Sample request constants
export const MAX_SAMPLE_ITEMS = 5;
export const SAMPLE_ELIGIBLE_UNITS = new Set(['18 oz', '1 lb', '2 lb', '5 lb']);

// Local Storage Keys
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER: 'user',
  PROFILE: 'profile',
  CART: 'drm_cart',
  SAMPLE_CART: 'drm_sample_cart',
};

// Configuration
export const CONFIG = {
  API_TIMEOUT: 30000, // 30 seconds
  TOKEN_REFRESH_BUFFER: 60000, // Refresh token 1 minute before expiry
};

// Application qualification label maps (must stay in sync with backend choices)
export const VOLUME_LABELS = {
  '50lb': 'Up to 50 lbs / month',
  '100lb': 'Up to 100 lbs / month',
  '500lb': 'Up to 500 lbs / month',
  '1000lb_plus': '1,000+ lbs / month',
};

export const FLOUR_TYPE_LABELS = {
  bread: 'Bread Flour',
  all_purpose: 'All Purpose',
  high_protein: 'High Protein',
  rye: 'Rye',
  spelt: 'Spelt',
  buckwheat: 'Buckwheat',
  cornmeal: 'Cornmeal / Grits',
  oats: 'Rolled Oats',
  other: 'Other',
};

// Employee products page size — must match LargePagePagination.max_page_size in backend/apps/common/pagination.py
export const EMPLOYEE_PRODUCTS_PAGE_SIZE = 500;

// Product Categories (must stay in sync with backend Product.CATEGORY_CHOICES)
export const PRODUCT_CATEGORIES = [
  { key: 'bread_flour', label: 'Bread Flour' },
  { key: 'pastry_flour', label: 'Pastry Flour' },
  { key: 'all_purpose_flour', label: 'All Purpose Flour' },
  { key: 'high_protein_flour', label: 'High Protein Flour' },
  { key: 'rye', label: 'Rye' },
  { key: 'oats', label: 'Oats' },
  { key: 'buckwheat', label: 'Buckwheat' },
  { key: 'cornmeal', label: 'Cornmeal' },
  { key: 'grits', label: 'Grits' },
  { key: 'mixes', label: 'Mixes' },
  { key: 'spelt', label: 'Spelt' },
  { key: 'einkorn', label: 'Einkorn' },
  { key: 'ancient_grain', label: 'Ancient Grain' },
  { key: 'popcorn', label: 'Popcorn' },
];

// Auth Events (for event-based auth error handling)
export const AUTH_EVENTS = {
  UNAUTHORIZED: 'auth:unauthorized',
  TOKEN_EXPIRED: 'auth:token-expired',
  LOGOUT: 'auth:logout',
};
